import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import PrintOverlayV4 from '../../print/PrintOverlayV4';
import usePrintSolutions from '../../print/usePrintSolutions';
import { PRINT_MAX_PROBLEMS, printLimitMessage } from '../../print/printRequestLimits';
import useErrnotePrintData from './useErrnotePrintData';

/**
 * ErrnotePrintOverlay — 오답노트 → 신 인쇄(v4) 어댑터.
 *
 * 데이터는 useErrnotePrintData 가 v4 계약으로 만들고, 이 컴포넌트는
 *  - 풀이 요청(onEnsureSolutions → fetchPrintSolutions, 시험지와 동일 파이프라인)
 *  - 인쇄 기록(onBeforePrint → POST /api/exams/reviews/print, 실패 시 인쇄 중단 — 구형 계약 유지)
 *  - 헤더 라벨(NOTE. {noteId})·푸터(카이테스트 · 오답노트)
 * 만 잇는다. 조판·용지·인쇄는 전부 PrintOverlayV4 소관이다.
 *
 * 사용처: 오답노트 화면(설정 모달의 「신규 인쇄」)·시험 결과 「테스트 관리」 모달. 구형
 * ErrorNotePrint 경로와 병행이다(printPreview 의 v2 병행 전례와 같은 도입 순서).
 */
function ErrnotePrintOverlay({ noteId, open, onClose }) {
  const { getAccessToken } = useAuth();
  const { problems, meta, loading } = useErrnotePrintData(noteId, open);
  const tooMany = problems.length > PRINT_MAX_PROBLEMS;

  // printPreview 의 fetchWithTokenRefresh 와 동일 계약: 성공 시 파싱된 json, 실패 시 throw
  const fetchJson = useCallback(async (url) => {
    const token = await getAccessToken();
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${url}`, {
      headers: { 'Authorization': token, 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }, [getAccessToken]);

  // 닫으면 풀이 요청 상태를 되돌린다 — 다음에 열 때 풀이 토글은 다시 명시적으로 켠다(v4 관례, 훅이 처리)
  const {
    solutions,
    loading: solutionsLoading,
    failedCount: solutionsFailedCount,
    setWanted: setWantSolutions,
    retry: retrySolutions,
  } = usePrintSolutions(problems, fetchJson, open);

  // 인쇄 직전 기록 — 구형과 동일 계약: 기록 실패 시 알리고 인쇄하지 않는다(throw → v4 가 중단)
  const handleBeforePrint = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/reviews/print`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token },
        body: JSON.stringify({ content_type: 'errnote', content_id: noteId }),
      });
      if (!response.ok) {
        throw new Error('인쇄 기록 생성 실패');
      }
    } catch (error) {
      console.error('인쇄 기록 생성 오류:', error);
      alert('인쇄 기록을 생성하는데 실패했습니다.');
      throw error;
    }
  }, [noteId, getAccessToken]);

  if (!open) return null;

  return (
    <>
      {/* 로드 중/빈 노트 안내 — printPreview v4 단독 경로와 같은 모양. z 는 v4 루트(9999990)와 동일 층 */}
      {(problems.length === 0 || tooMany) && createPortal(
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 9999990 }}>
          <div className="absolute inset-0 bg-black/30" onClick={onClose}></div>
          <div className="relative z-10 flex items-center gap-3 rounded-2xl bg-white px-6 py-4 shadow-lg">
            {loading ? (
              <>
                <div className="size-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary" />
                <span className="text-sm text-gray-600">오답노트 준비 중...</span>
              </>
            ) : (
              <>
                <span className="text-sm text-gray-600">{tooMany ? printLimitMessage(problems.length) : '인쇄할 문제가 없습니다.'}</span>
                <button onClick={onClose} className="rounded-full border border-gray-300 px-3 py-1 text-xs hover:bg-gray-100">
                  닫기
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
      <PrintOverlayV4
        printContext={{ kind: 'note', id: noteId }}
        open={problems.length > 0 && !tooMany}
        onClose={onClose}
        problems={problems}
        solutions={solutions}
        solutionsLoading={solutionsLoading}
        solutionsFailedCount={solutionsFailedCount}
        onRetrySolutions={retrySolutions}
        onEnsureSolutions={() => setWantSolutions(true)}
        onBeforePrint={handleBeforePrint}
        meta={{
          tree_name: meta?.tree_name || '',
          edu_ver: meta?.edu_ver || '',
          student_name: meta?.student_name || '',
          exam_id: noteId,
          idLabel: 'NOTE',
          leftFooter: '카이테스트 · 오답노트',
        }}
      />
    </>
  );
}

export default ErrnotePrintOverlay;
